import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  TrendingUp, TrendingDown, Target, Phone, Clock, Award, Calendar,
  ArrowUp, ArrowDown, Minus, RefreshCw, Star, Trophy, BarChart3, ExternalLink,
  Sparkles, CheckCircle, Brain, Loader2, Zap, ChevronRight
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer
} from 'recharts';
import api, { getUserTrends, getCallTypeDistribution, getAnalyticsLeaderboard, getCalls, getUserPerformanceAnalysis } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AnalyticsCardPopup, ClickableAnalyticsCard } from '@/components/analytics/AnalyticsCardPopup';

const CALL_TYPE_COLORS = {
  job_outreach: '#3b82f6',
  submission_call: '#10b981',
  pre_screening: '#8b5cf6',
  spotlight_call: '#f59e0b',
  interview_prep: '#ec4899',
  other: '#94a3b8'
};

export default function UserAnalytics() {
  const { userUrl } = useParams();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [user, setUser] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [userTrends, setUserTrends] = useState(null);
  const [callTypeDistribution, setCallTypeDistribution] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [userRank, setUserRank] = useState(null);
  
  // Popup states for clickable cards
  const [popupOpen, setPopupOpen] = useState(null);
  const [popupData, setPopupData] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupPage, setPopupPage] = useState(1);
  const [popupTotal, setPopupTotal] = useState(0);
  const [popupDuration, setPopupDuration] = useState('30d');
  const POPUP_PAGE_SIZE = 15;

  // My Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [analysisDays, setAnalysisDays] = useState(30);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const ANALYSIS_PERIODS = [
    { value: 7, label: 'Last 7 Days' },
    { value: 14, label: 'Last 14 Days' },
    { value: 30, label: 'Last 30 Days' },
    { value: 60, label: 'Last 60 Days' },
    { value: 90, label: 'Last 90 Days' },
  ];

  const isDark = theme === 'dark';
  const textColor = isDark ? '#e2e8f0' : '#1e293b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';

  useEffect(() => {
    loadUserData();
  }, [userUrl]);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, period]);

  const loadUserData = async () => {
    try {
      const res = await api.get(`/users/url/${userUrl}`);
      setUser(res.data);
      // Handle both tenant_id and tenant.id structures
      const tId = res.data.tenant_id || res.data.tenant?.id;
      setTenantId(tId);
    } catch (error) {
      console.error('Error loading user:', error);
      toast.error('Failed to load user data');
    }
  };

  const loadAnalytics = async () => {
    // Handle both _id and id for user identification
    const userId = user?._id || user?.id;
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const [trendsRes, distRes, leaderRes] = await Promise.all([
        getUserTrends(userId, tenantId || '', period),
        getCallTypeDistribution(tenantId || '', period, { userId: userId }),
        getAnalyticsLeaderboard(tenantId || '', period, 'score', 50)
      ]);

      setUserTrends(trendsRes.data);
      setCallTypeDistribution(distRes.data);
      setLeaderboard(leaderRes.data);

      // Find user's rank - check both _id and id
      const rank = leaderRes.data.leaderboard.find(u => u.user_id === userId);
      setUserRank(rank);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Load performance analysis
  const loadAnalysis = async (days) => {
    const userId = user?._id || user?.id;
    setAnalysisLoading(true);
    try {
      const { data } = await getUserPerformanceAnalysis(userId, days);
      setAnalysis(data);
    } catch (error) {
      console.error('Failed to load analysis:', error);
      setAnalysis({ has_data: false, summary: 'Unable to load analysis', strengths: [], improvements: [] });
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Load analysis when user or days change
  useEffect(() => {
    const userId = user?._id || user?.id;
    if (userId && !loading) {
      loadAnalysis(analysisDays);
    }
  }, [user, analysisDays, loading]);
  
  // Popup handlers for clickable cards
  const openPopup = async (type) => {
    setPopupOpen(type);
    setPopupPage(1);
    setPopupDuration(period);
    await loadPopupData(type, 1, period);
  };
  
  const loadPopupData = async (type, page, dur) => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    
    setPopupLoading(true);
    try {
      switch (type) {
        case 'calls':
          const callsRes = await getCalls({ page, limit: POPUP_PAGE_SIZE, period: dur, user_id: userId });
          setPopupData(callsRes.data.calls || []);
          setPopupTotal(callsRes.data.total || 0);
          break;
        case 'score':
          // Score trends - use the user trends data
          if (userTrends?.data) {
            setPopupData(userTrends.data);
            setPopupTotal(userTrends.data.length);
          }
          break;
        case 'rank':
          // Leaderboard data
          if (leaderboard?.leaderboard) {
            setPopupData(leaderboard.leaderboard);
            setPopupTotal(leaderboard.leaderboard.length);
          }
          break;
        case 'best':
          // Best calls - filter for score >= 8
          const bestRes = await getCalls({ page, limit: POPUP_PAGE_SIZE, period: dur, user_id: userId, min_score: 8 });
          setPopupData(bestRes.data.calls || []);
          setPopupTotal(bestRes.data.total || 0);
          break;
        case 'distribution':
          // Call type distribution
          if (callTypeDistribution?.distribution) {
            setPopupData(callTypeDistribution.distribution);
            setPopupTotal(callTypeDistribution.distribution.length);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading popup data:', error);
      toast.error('Failed to load detailed data');
    } finally {
      setPopupLoading(false);
    }
  };
  
  const handlePopupPageChange = (newPage) => {
    setPopupPage(newPage);
    if (popupOpen === 'calls' || popupOpen === 'best') {
      loadPopupData(popupOpen, newPage, popupDuration);
    }
  };
  
  const handlePopupDurationChange = (newDuration) => {
    setPopupDuration(newDuration);
    setPopupPage(1);
    loadPopupData(popupOpen, 1, newDuration);
  };
  
  const closePopup = () => {
    setPopupOpen(null);
    setPopupData([]);
    setPopupPage(1);
  };
  
  // Column definitions for popup tables
  const popupColumns = {
    calls: [
      { key: 'filename', label: 'Call', render: (v, row) => (
        <a 
          href={`/user/${userUrl}/call/${row.id}`} 
          className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
        >
          {v?.substring(0, 30)}{v?.length > 30 ? '...' : ''}
          <ExternalLink className="h-3 w-3" />
        </a>
      )},
      { key: 'call_type', label: 'Type', render: (v) => <Badge variant="outline" className="capitalize">{v?.replace('_', ' ')}</Badge> },
      { key: 'average_score', label: 'Score', render: (v) => <Badge className={v >= 7 ? 'bg-green-100 text-green-700' : v >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>{v?.toFixed(1) || '-'}</Badge> },
      { key: 'duration', label: 'Duration', render: (v) => `${Math.floor((v || 0) / 60)}:${String((v || 0) % 60).padStart(2, '0')}` },
      { key: 'call_date', label: 'Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' }
    ],
    best: [
      { key: 'filename', label: 'Call', render: (v, row) => (
        <a 
          href={`/user/${userUrl}/call/${row.id}`} 
          className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
        >
          {v?.substring(0, 30)}{v?.length > 30 ? '...' : ''}
          <ExternalLink className="h-3 w-3" />
        </a>
      )},
      { key: 'call_type', label: 'Type', render: (v) => <Badge variant="outline" className="capitalize">{v?.replace('_', ' ')}</Badge> },
      { key: 'average_score', label: 'Score', render: (v) => <Badge className="bg-green-100 text-green-700">{v?.toFixed(1) || '-'}</Badge> },
      { key: 'duration', label: 'Duration', render: (v) => `${Math.floor((v || 0) / 60)}:${String((v || 0) % 60).padStart(2, '0')}` },
      { key: 'call_date', label: 'Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' }
    ],
    score: [
      { key: 'date', label: 'Date' },
      { key: 'avg_score', label: 'Your Score', render: (v) => v?.toFixed(1) || '-' },
      { key: 'team_avg_score', label: 'Team Avg', render: (v) => v?.toFixed(1) || '-' },
      { key: 'total_calls', label: 'Calls' },
      { key: 'total_duration', label: 'Duration (min)', render: (v) => Math.round((v || 0) / 60) }
    ],
    rank: [
      { key: 'rank', label: 'Rank', render: (v) => <Badge variant={v <= 3 ? 'default' : 'outline'}>{v}</Badge> },
      { key: 'name', label: 'User' },
      { key: 'total_calls', label: 'Calls' },
      { key: 'avg_score', label: 'Avg Score', render: (v) => <span className="font-semibold">{v?.toFixed(1)}</span> },
      { key: 'best_calls', label: 'Best Calls' }
    ],
    distribution: [
      { key: 'call_type', label: 'Call Type', render: (v) => <span className="capitalize">{v?.replace('_', ' ')}</span> },
      { key: 'count', label: 'Count' },
      { key: 'percentage', label: 'Percentage', render: (v) => `${v?.toFixed(1)}%` },
      { key: 'avg_score', label: 'Avg Score', render: (v) => v?.toFixed(1) || '-' }
    ]
  };
  
  // Popup title and descriptions
  const popupInfo = {
    calls: { title: 'My Calls', description: 'All your analyzed calls' },
    best: { title: 'Best Calls', description: 'Your top-performing calls (score ≥ 8)' },
    score: { title: 'Score Trends', description: 'Your score history compared to team' },
    rank: { title: 'Team Leaderboard', description: 'Performance rankings across the team' },
    distribution: { title: 'Call Type Distribution', description: 'Breakdown of your calls by type' }
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <ArrowUp className="h-3 w-3 text-emerald-500" />;
    if (change < 0) return <ArrowDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-muted-foreground" />;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (trend === 'declining') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  if (loading && !userTrends) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-analytics">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className="text-xl">{user?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Analytics</h1>
            <p className="text-muted-foreground">Track your performance and growth</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="12m">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadAnalytics} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Metrics - Clickable Cards */}
      {userTrends && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ClickableAnalyticsCard onClick={() => openPopup('calls')}>
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-3xl font-bold text-foreground">{userTrends.summary.total_calls}</p>
                  </div>
                  <div className="p-3 bg-blue-500/20 rounded-xl">
                    <Phone className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-blue-600 dark:text-blue-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view all calls</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('score')}>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                    <p className="text-3xl font-bold text-foreground">{userTrends.summary.avg_score}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {getTrendIcon(userTrends.summary.trend)}
                      <span className="text-sm text-muted-foreground capitalize">{userTrends.summary.trend}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-emerald-500/20 rounded-xl">
                    <Target className="h-6 w-6 text-emerald-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-emerald-600 dark:text-emerald-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view trends</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('rank')}>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Your Rank</p>
                    <p className="text-3xl font-bold text-foreground">
                      #{userRank?.rank || '-'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      of {leaderboard?.leaderboard.length || 0} team members
                    </p>
                  </div>
                  <div className="p-3 bg-amber-500/20 rounded-xl">
                    <Trophy className="h-6 w-6 text-amber-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-amber-600 dark:text-amber-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view leaderboard</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>

          <ClickableAnalyticsCard onClick={() => openPopup('best')}>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 h-full">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Best Calls</p>
                    <p className="text-3xl font-bold text-foreground">{userRank?.best_calls || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Score ≥ 8.0</p>
                  </div>
                  <div className="p-3 bg-purple-500/20 rounded-xl">
                    <Star className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 text-xs text-purple-600 dark:text-purple-400">
                  <ExternalLink className="h-3 w-3" />
                  <span>Click to view best calls</span>
                </div>
              </CardContent>
            </Card>
          </ClickableAnalyticsCard>
        </div>
      )}

      {/* My Analysis Card */}
      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-purple-200 dark:border-purple-800" data-testid="my-analysis-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              My Analysis
            </CardTitle>
            <CardDescription>AI-powered performance insights based on your calls</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={analysisDays.toString()} onValueChange={(v) => setAnalysisDays(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="analysis-period-select">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {ANALYSIS_PERIODS.map(p => (
                  <SelectItem key={p.value} value={p.value.toString()}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => loadAnalysis(analysisDays)}
              disabled={analysisLoading}
              data-testid="refresh-analysis-btn"
            >
              {analysisLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {analysisLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
              </div>
            </div>
          ) : analysis?.has_data ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="p-4 bg-white/70 dark:bg-slate-800/70 rounded-lg border border-purple-100 dark:border-purple-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{analysis.summary}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>{analysis.stats?.total_calls || 0} calls analyzed</span>
                      <span>Avg Score: {analysis.stats?.avg_score || 0}/10</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strengths and Improvements Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Strengths */}
                <div className="p-4 bg-emerald-50/70 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <h4 className="font-medium text-sm text-emerald-800 dark:text-emerald-200">Your Strengths</h4>
                  </div>
                  {analysis.strengths?.length > 0 ? (
                    <ul className="space-y-2">
                      {analysis.strengths.slice(0, 4).map((strength, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-300">
                          <span className="text-emerald-500 mt-0.5">•</span>
                          {strength}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground">Complete more calls to identify your strengths</p>
                  )}
                </div>

                {/* Areas of Improvement */}
                <div className="p-4 bg-amber-50/70 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="h-4 w-4 text-amber-600" />
                    <h4 className="font-medium text-sm text-amber-800 dark:text-amber-200">Areas to Improve</h4>
                  </div>
                  {analysis.improvements?.length > 0 ? (
                    <ul className="space-y-2">
                      {analysis.improvements.slice(0, 4).map((improvement, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                          <span className="text-amber-500 mt-0.5">•</span>
                          {improvement}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground">No specific improvements identified yet</p>
                  )}
                </div>
              </div>

              {/* Link to Communication Coach */}
              <div className="flex items-center justify-between p-3 bg-blue-50/70 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    This analysis feeds into your <strong>AI Communication Coach</strong> for personalized training
                  </span>
                </div>
                <Link 
                  to={`/user/${userUrl}/ai-coach`}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium flex items-center gap-1"
                >
                  Try Coach
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Brain className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No analysis available for this period</p>
              <p className="text-xs text-muted-foreground">Complete some calls to see your performance analysis</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance vs Team */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Your Score vs Team Average
            </CardTitle>
            <CardDescription>Track how you compare to the team over time</CardDescription>
          </CardHeader>
          <CardContent>
            {userTrends && userTrends.data.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userTrends.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} />
                  <YAxis domain={[0, 10]} tick={{ fill: textColor, fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#1e293b' : '#fff',
                      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="avg_score" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    name="Your Score"
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="team_avg_score" 
                    stroke="#94a3b8" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Team Average"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No trend data available for selected period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calls by Type */}
        <Card>
          <CardHeader>
            <CardTitle>Your Call Types</CardTitle>
            <CardDescription>Distribution of your calls by type</CardDescription>
          </CardHeader>
          <CardContent>
            {callTypeDistribution && callTypeDistribution.distribution.length > 0 ? (
              <div className="space-y-4">
                {callTypeDistribution.distribution.slice(0, 6).map((ct) => (
                  <div key={ct.call_type} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{ct.name}</span>
                      <span className="text-muted-foreground">{ct.count} calls ({ct.percentage}%)</span>
                    </div>
                    <div className="relative">
                      <Progress 
                        value={ct.percentage} 
                        className="h-2"
                        style={{ 
                          '--progress-color': CALL_TYPE_COLORS[ct.call_type] || '#94a3b8'
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Avg Score: {ct.avg_score}</span>
                      <span>{ct.total_duration_hours}h total</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No call type data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Team Comparison Card */}
      {userRank && leaderboard && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              Your Position in the Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Before You */}
              <div className={cn(
                "p-4 rounded-lg border",
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-muted/50 border-border"
              )}>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Ahead of You</h4>
                {userRank.rank > 1 ? (
                  <div className="space-y-2">
                    {leaderboard.leaderboard
                      .filter(u => u.rank < userRank.rank)
                      .slice(-3)
                      .reverse()
                      .map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">#{u.rank}</span>
                            <Avatar className="h-6 w-6">
                              <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{u.name}</span>
                          </div>
                          <Badge variant="secondary">{u.avg_score}</Badge>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">You&apos;re #1! 🏆</p>
                )}
              </div>

              {/* You */}
              <div className="p-4 rounded-lg border-2 border-blue-500 bg-blue-500/10">
                <h4 className="text-sm font-medium text-blue-500 mb-3">You</h4>
                <div className="text-center py-4">
                  <Avatar className="h-16 w-16 mx-auto border-4 border-blue-500">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <p className="font-bold mt-2">{user?.name}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Badge className="text-lg px-3 py-1">#{userRank.rank}</Badge>
                    <span className="text-2xl font-bold">{userRank.avg_score}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{userRank.total_calls} calls</p>
                </div>
              </div>

              {/* After You */}
              <div className={cn(
                "p-4 rounded-lg border",
                isDark ? "bg-slate-800/50 border-slate-700" : "bg-muted/50 border-border"
              )}>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Behind You</h4>
                {userRank.rank < leaderboard.leaderboard.length ? (
                  <div className="space-y-2">
                    {leaderboard.leaderboard
                      .filter(u => u.rank > userRank.rank)
                      .slice(0, 3)
                      .map((u) => (
                        <div key={u.user_id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">#{u.rank}</span>
                            <Avatar className="h-6 w-6">
                              <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{u.name}</span>
                          </div>
                          <Badge variant="secondary">{u.avg_score}</Badge>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Keep pushing forward!</p>
                )}
              </div>
            </div>

            {/* Progress to next rank */}
            {userRank.rank > 1 && (
              <div className="mt-6 p-4 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Progress to Rank #{userRank.rank - 1}</span>
                  <span className="text-sm text-muted-foreground">
                    Need +{(leaderboard.leaderboard.find(u => u.rank === userRank.rank - 1)?.avg_score - userRank.avg_score).toFixed(2)} points
                  </span>
                </div>
                <Progress 
                  value={Math.min(100, (userRank.avg_score / (leaderboard.leaderboard.find(u => u.rank === userRank.rank - 1)?.avg_score || 10)) * 100)} 
                  className="h-2"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Performance */}
      {userTrends && userTrends.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Performance</CardTitle>
            <CardDescription>Your calls and scores day by day</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={userTrends.data}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fill: textColor, fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 10]} tick={{ fill: textColor, fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Calls" />
                <Line yAxisId="right" type="monotone" dataKey="avg_score" stroke="#10b981" strokeWidth={2} name="Score" dot={{ fill: '#10b981' }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      
      {/* Analytics Card Popup Modals */}
      {popupOpen && popupInfo[popupOpen] && (
        <AnalyticsCardPopup
          isOpen={!!popupOpen}
          onClose={closePopup}
          title={popupInfo[popupOpen].title}
          description={popupInfo[popupOpen].description}
          data={popupData}
          columns={popupColumns[popupOpen] || []}
          loading={popupLoading}
          totalItems={popupTotal}
          itemsPerPage={POPUP_PAGE_SIZE}
          currentPage={popupPage}
          onPageChange={handlePopupPageChange}
          duration={popupDuration}
          onDurationChange={handlePopupDurationChange}
          exportFilename={`my_analytics_${popupOpen}`}
        />
      )}
    </div>
  );
}
